import { DateTime } from "luxon";

import { ajaxController } from "./controller/AjaxController";
import { OptionResponse } from "../DTOs/Common/OptionResponse";
import { StoppageDto } from "../DTOs/Stoppage/StoppageDto";
import { PagedResult } from "../DTOs/Common/PagedResult";
import { StoppageSubTypeDto } from "../DTOs/Stoppage/StoppageSubTypeDto";
import { StoppageImageDTO } from "../DTOs/Stoppage/StoppageImageDTO";
import { DataTableFilteredRequest } from "../components/DataTable/TableHook";
import { CreateMultipleStoppagesRequest, StoppagePreset } from "../DTOs/Stoppage/MultipleStoppages";
import { DoubleShift, TripleShift } from "../types/Shift";

export interface PlantStoppagesGroup {
  plantId: number
  stoppages: PagedResult<StoppageDto>
}

export class StoppagesAjax {
  private _ajaxController = ajaxController;
  private _stoppagesDataEndpoint = "/Stoppages";

  async getStoppagesByShiftAndPlant(shiftDate: DateTime, dayNightAfternoon: TripleShift, plantId: number) {
    return this._ajaxController.get<PagedResult<StoppageDto>>(
      `${this._stoppagesDataEndpoint}/stoppages/${plantId}/${shiftDate.toISODate()}/${dayNightAfternoon[0]}`,
      {},
      x => PagedResult.toPagedResult(x, StoppageDto)
    );
  }

  // Note: shift-wide batch used to warm every per-plant plantStoppages cache from one request
  // instead of one request per plant.
  async getStoppagesForShift(shiftDate: DateTime, dayNightAfternoon: TripleShift) {
    return this._ajaxController.get<PlantStoppagesGroup[]>(
      `${this._stoppagesDataEndpoint}/stoppages/shift/${shiftDate.toISODate()}/${dayNightAfternoon[0]}`,
      {},
      (raw: any[]) => raw.map(g => ({
        plantId: g.plantId,
        stoppages: PagedResult.toPagedResult(g.stoppages, StoppageDto)
      }))
    );
  }

  async getStoppage(id: number) {
    return this._ajaxController.get<StoppageDto>(
      `${this._stoppagesDataEndpoint}/stoppages/${id}`,
      {}
    );
  }

  async addStoppage(data: StoppageDto) {
    return this._ajaxController.post<StoppageDto, StoppageDto>(
      `${this._stoppagesDataEndpoint}/stoppages`,
      data
    );
  }

  async deleteStoppage(id: number) {
    return this._ajaxController.delete(
      `${this._stoppagesDataEndpoint}/stoppages/${id}`
    );
  }

  async updateStoppage(data: StoppageDto) {
    return this._ajaxController.put<StoppageDto>(
      `${this._stoppagesDataEndpoint}/stoppages`,
      data
    );
  }

  async getStoppagePresets(shiftDate: DateTime, dayOrNight: DoubleShift) {
    return this._ajaxController.get<StoppagePreset[]>(
      `${this._stoppagesDataEndpoint}/stoppages/presets/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      {}
    );
  }

  async insertMultipleStoppages(request: CreateMultipleStoppagesRequest) {
    return this._ajaxController.post<CreateMultipleStoppagesRequest, boolean>(
      `${this._stoppagesDataEndpoint}/stoppages/multiple`, request
    );
  }

  async getStoppagesMaintenance(request: DataTableFilteredRequest) {
    const x = this._ajaxController.get<PagedResult<StoppageDto>>(
      `${this._stoppagesDataEndpoint}/stoppages/maintenance`,
      request
    );
    return x;
  }

  async getStoppageImages(stoppageId: number) {
    return this._ajaxController.get<PagedResult<StoppageImageDTO>>(
      `${this._stoppagesDataEndpoint}/stoppages/${stoppageId}/images`, {}
    );
  }

  async uploadStoppageImage(stoppageId: number, formData: FormData) {
    return this._ajaxController.post<FormData, StoppageDto>(
      `${this._stoppagesDataEndpoint}/stoppages/${stoppageId}/images`,
      formData
    );
  }

  async deleteStoppageImage(imageId: number) {
    return this._ajaxController.post<null, StoppageDto>(
      `${this._stoppagesDataEndpoint}/stoppages/images/${imageId}`,
      null
    );
  }

  async getStoppageTypes() {
    return this._ajaxController.get<OptionResponse>(
      `${this._stoppagesDataEndpoint}/StoppageTypes`,
      {}
    );
  }

  async getStoppageSubtypes(filters: DataTableFilteredRequest) {
    return this._ajaxController.get<PagedResult<StoppageSubTypeDto>>(
      `${this._stoppagesDataEndpoint}/subtypes`,
      filters
    );
  }

  async updateStoppageSubType(data: StoppageSubTypeDto) {
    return this._ajaxController.put<StoppageSubTypeDto>(
      `${this._stoppagesDataEndpoint}/subtypes/${data.stoppageSubTypeId}`,
      data
    );
  }

  async addStoppageSubType(data: StoppageSubTypeDto) {
    return this._ajaxController.post<StoppageSubTypeDto, StoppageSubTypeDto>(
      `${this._stoppagesDataEndpoint}/subtypes`,
      data
    );
  }

  async deleteStoppageSubType(id: number) {
    return this._ajaxController.delete(
      `${this._stoppagesDataEndpoint}/subtypes/${id}`
    );
  }
}

export const stoppagesAjax = new StoppagesAjax();